import { Badge, Button, Card, Input, LoadingContainer, Modal, Select } from '@/components/ui';
import { CambiarCombinacionNtcModal } from '@/components/sysadmin/CambiarCombinacionNtcModal';
import { CrearEmpresaWizard } from '@/components/sysadmin/CrearEmpresaWizard';
import { ColumnPicker, useColumnVisibility, type ColDef } from '@/components/ui/ColumnPicker';
import {
  useAsesores,
  useAsesoriasSinEvaluacionAprobada,
  useCombinaciones,
  useEmpresasDesincronizadas,
  useEmpresasHorasDesincronizadas,
  useEmpresasSysadmin,
  useRecalcularHorasEmpresa,
  useSincronizarEmpresa,
} from '@/hooks/useSysadmin';
import type { EmpresaSysadmin } from '@/services/sysadmin.service';
import {
  AlertTriangle,
  Check,
  Copy,
  Link2,
  MoreVertical,
  Plus,
  Power,
  PowerOff,
  RefreshCw,
  RotateCcw,
  Search,
  UserCheck,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

type DesincronizadaColKey = 'empresa' | 'porcentaje' | 'sinAprobar' | 'acciones';

const DESINCRONIZADAS_COLS: ColDef<DesincronizadaColKey>[] = [
  { key: 'empresa', label: 'Empresa', defaultVisible: true },
  { key: 'porcentaje', label: '% cumplimiento', defaultVisible: true },
  { key: 'sinAprobar', label: 'Asesorias sin aprobar', defaultVisible: true },
  { key: 'acciones', label: 'Acciones', defaultVisible: true },
];

type HorasDesincronizadaColKey = 'empresa' | 'horas' | 'acciones';

const HORAS_DESINCRONIZADAS_COLS: ColDef<HorasDesincronizadaColKey>[] = [
  { key: 'empresa', label: 'Empresa', defaultVisible: true },
  { key: 'horas', label: 'Horas asesoria', defaultVisible: true },
  { key: 'acciones', label: 'Acciones', defaultVisible: true },
];

type SinEvaluacionColKey = 'empresa' | 'asesoria' | 'fecha' | 'horas' | 'evaluacion';

const SIN_EVALUACION_COLS: ColDef<SinEvaluacionColKey>[] = [
  { key: 'empresa', label: 'Empresa', defaultVisible: true },
  { key: 'asesoria', label: 'Asesoria', defaultVisible: true },
  { key: 'fecha', label: 'Fecha fin', defaultVisible: true },
  { key: 'horas', label: 'Horas', defaultVisible: true },
  { key: 'evaluacion', label: 'Estado evaluacion', defaultVisible: true },
];

type MainEmpresasColKey = 'nombre' | 'nit' | 'nitNombre' | 'asesor' | 'combinacion' | 'estado' | 'acciones';

const MAIN_EMPRESAS_COLS: ColDef<MainEmpresasColKey>[] = [
  { key: 'nombre', label: 'Empresa', defaultVisible: true },
  { key: 'nit', label: 'NIT', defaultVisible: true },
  { key: 'nitNombre', label: 'NIT - Empresa', defaultVisible: true },
  { key: 'asesor', label: 'Asesor', defaultVisible: true },
  { key: 'combinacion', label: 'Combinación NTC', defaultVisible: true },
  { key: 'estado', label: 'Estado', defaultVisible: true },
  { key: 'acciones', label: 'Acciones', defaultVisible: true },
];

export function GestionEmpresasPage() {
  const {
    empresas,
    isLoading,
    error,
    asignarAsesor,
    asignarCombinacion,
    crearEmpresa,
    cambiarCombinacionNtc,
    eliminarEmpresa,
    reactivarEmpresa,
  } = useEmpresasSysadmin();
  const { asesores, isLoading: loadingAsesores } = useAsesores();
  const { combinaciones, isLoading: loadingCombinaciones } = useCombinaciones();
  const { isLoading: isSincronizandoEmpresa, sincronizar: sincronizarEmpresa } = useSincronizarEmpresa();
  const {
    empresas: desincronizadas,
    isLoading: isLoadingDesincronizadas,
    error: errorDesincronizadas,
    buscar: buscarDesincronizadas,
    removeEmpresa: removeDesincronizada,
  } = useEmpresasDesincronizadas();
  const {
    empresas: horasDesincronizadas,
    isLoading: isLoadingHorasDesincronizadas,
    error: errorHorasDesincronizadas,
    buscar: buscarHorasDesincronizadas,
    removeEmpresa: removeHorasDesincronizada,
  } = useEmpresasHorasDesincronizadas();
  const { isLoading: isRecalculandoHoras, recalcular: recalcularHoras } = useRecalcularHorasEmpresa();
  const {
    asesorias: asesoriasSinEvaluacion,
    isLoading: isLoadingSinEvaluacion,
    error: errorSinEvaluacion,
    buscar: buscarAsesoriasSinEvaluacion,
  } = useAsesoriasSinEvaluacionAprobada();
  const menuRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');
  const [selectedAsesorFilter, setSelectedAsesorFilter] = useState<string>('all');
  const [showAsesorModal, setShowAsesorModal] = useState(false);
  const [showCombinacionModal, setShowCombinacionModal] = useState(false);
  const [showCrearWizard, setShowCrearWizard] = useState(false);
  const [showCambiarNtcModal, setShowCambiarNtcModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReactivarModal, setShowReactivarModal] = useState(false);
  const [showSincronizarModal, setShowSincronizarModal] = useState(false);

  const [selectedEmpresa, setSelectedEmpresa] = useState<EmpresaSysadmin | null>(null);
  const [selectedAsesorId, setSelectedAsesorId] = useState<string>('');
  const [selectedCombinacionId, setSelectedCombinacionId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [openMenuNit, setOpenMenuNit] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [hasSearchedDesincronizadas, setHasSearchedDesincronizadas] = useState(false);
  const [recalculatingNit, setRecalculatingNit] = useState<string | null>(null);
  const [recalcularError, setRecalcularError] = useState<string | null>(null);
  const [hasSearchedHorasDesincronizadas, setHasSearchedHorasDesincronizadas] = useState(false);
  const [recalculatingHorasNit, setRecalculatingHorasNit] = useState<string | null>(null);
  const [recalcularHorasError, setRecalcularHorasError] = useState<string | null>(null);
  const [hasSearchedSinEvaluacion, setHasSearchedSinEvaluacion] = useState(false);

  const {
    visible: colVisibleDesincronizadas,
    toggle: toggleColDesincronizadas,
    reset: resetColsDesincronizadas,
    activeCount: colCountDesincronizadas,
  } = useColumnVisibility<DesincronizadaColKey>(DESINCRONIZADAS_COLS, 'tourixia:sysadmin-desincronizadas-cols');

  const {
    visible: colVisibleHorasDesincronizadas,
    toggle: toggleColHorasDesincronizadas,
    reset: resetColsHorasDesincronizadas,
    activeCount: colCountHorasDesincronizadas,
  } = useColumnVisibility<HorasDesincronizadaColKey>(HORAS_DESINCRONIZADAS_COLS, 'tourixia:sysadmin-horas-desincronizadas-cols');

  const {
    visible: colVisibleSinEvaluacion,
    toggle: toggleColSinEvaluacion,
    reset: resetColsSinEvaluacion,
    activeCount: colCountSinEvaluacion,
  } = useColumnVisibility<SinEvaluacionColKey>(SIN_EVALUACION_COLS, 'tourixia:sysadmin-sin-evaluacion-cols');

  const {
    visible: colVisibleMain,
    toggle: toggleColMain,
    reset: resetColsMain,
    activeCount: colCountMain,
  } = useColumnVisibility<MainEmpresasColKey>(MAIN_EMPRESAS_COLS, 'tourixia:sysadmin-empresas-main-cols');

  const [copiedList, setCopiedList] = useState(false);
  const [copiedNitNombreKey, setCopiedNitNombreKey] = useState<string | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuNit(null);
      }
    }

    if (openMenuNit !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuNit]);

  const filteredEmpresas = useMemo(() => {
    const q = search.trim().toLowerCase();
    return empresas.filter((e) => {
      const matchesSearch = !q || e.nombre.toLowerCase().includes(q) || e.nitId.toLowerCase().includes(q);
      const matchesAsesor =
        selectedAsesorFilter === 'all'
          ? true
          : selectedAsesorFilter === 'unassigned'
            ? !e.asesorId
            : e.asesorId?.toString() === selectedAsesorFilter;

      return matchesSearch && matchesAsesor;
    });
  }, [empresas, search, selectedAsesorFilter]);

  const openAsesorModal = (empresa: EmpresaSysadmin) => {
    setSelectedEmpresa(empresa);
    setSelectedAsesorId(empresa.asesorId?.toString() || '');
    setActionError(null);
    setOpenMenuNit(null);
    setShowAsesorModal(true);
  };

  const openCombinacionModal = (empresa: EmpresaSysadmin) => {
    setSelectedEmpresa(empresa);
    setSelectedCombinacionId(empresa.combinacionNtcId?.toString() || '');
    setActionError(null);
    setOpenMenuNit(null);
    setShowCombinacionModal(true);
  };

  const openCambiarNtcModal = (empresa: EmpresaSysadmin) => {
    setSelectedEmpresa(empresa);
    setOpenMenuNit(null);
    setShowCambiarNtcModal(true);
  };

  const openDeleteModal = (empresa: EmpresaSysadmin) => {
    setSelectedEmpresa(empresa);
    setActionError(null);
    setOpenMenuNit(null);
    setShowDeleteModal(true);
  };

  const openReactivarModal = (empresa: EmpresaSysadmin) => {
    setSelectedEmpresa(empresa);
    setActionError(null);
    setOpenMenuNit(null);
    setShowReactivarModal(true);
  };

  const openSincronizarModal = (empresa: EmpresaSysadmin) => {
    setSelectedEmpresa(empresa);
    setActionError(null);
    setOpenMenuNit(null);
    setShowSincronizarModal(true);
  };

  const handleSincronizarEmpresa = async () => {
    if (!selectedEmpresa) return;
    setSaving(true);
    setActionError(null);
    try {
      await sincronizarEmpresa(selectedEmpresa.nitId);
      setShowSincronizarModal(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al sincronizar empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleBuscarDesincronizadas = async () => {
    try {
      await buscarDesincronizadas();
    } catch {
      // el error ya queda expuesto en errorDesincronizadas
    } finally {
      setHasSearchedDesincronizadas(true);
    }
  };

  const handleRecalcularDesincronizada = async (nit: string) => {
    setRecalcularError(null);
    setRecalculatingNit(nit);
    try {
      await sincronizarEmpresa(nit);
      removeDesincronizada(nit);
    } catch (err) {
      setRecalcularError(err instanceof Error ? err.message : 'Error al recalcular empresa');
    } finally {
      setRecalculatingNit(null);
    }
  };

  const handleBuscarHorasDesincronizadas = async () => {
    try {
      await buscarHorasDesincronizadas();
    } catch {
      // el error ya queda expuesto en errorHorasDesincronizadas
    } finally {
      setHasSearchedHorasDesincronizadas(true);
    }
  };

  const handleRecalcularHoras = async (nit: string) => {
    setRecalcularHorasError(null);
    setRecalculatingHorasNit(nit);
    try {
      await recalcularHoras(nit);
      removeHorasDesincronizada(nit);
    } catch (err) {
      setRecalcularHorasError(err instanceof Error ? err.message : 'Error al recalcular horas');
    } finally {
      setRecalculatingHorasNit(null);
    }
  };

  const handleBuscarSinEvaluacion = async () => {
    try {
      await buscarAsesoriasSinEvaluacion();
    } catch {
      // el error ya queda expuesto en errorSinEvaluacion
    } finally {
      setHasSearchedSinEvaluacion(true);
    }
  };

  const handleAsignarAsesor = async () => {
    if (!selectedEmpresa) return;
    setSaving(true);
    setActionError(null);
    try {
      const asesorId = selectedAsesorId ? parseInt(selectedAsesorId) : null;
      await asignarAsesor(selectedEmpresa.nitId, asesorId);
      setShowAsesorModal(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al asignar asesor');
    } finally {
      setSaving(false);
    }
  };

  const handleAsignarCombinacion = async () => {
    if (!selectedEmpresa) return;
    setSaving(true);
    setActionError(null);
    try {
      const combinacionId = selectedCombinacionId ? parseInt(selectedCombinacionId) : null;
      await asignarCombinacion(selectedEmpresa.nitId, combinacionId);
      setShowCombinacionModal(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al asignar combinación');
    } finally {
      setSaving(false);
    }
  };

  const handleEliminarEmpresa = async () => {
    if (!selectedEmpresa) return;
    setSaving(true);
    setActionError(null);
    try {
      await eliminarEmpresa(selectedEmpresa.nitId);
      setShowDeleteModal(false);
      setSelectedEmpresa(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al desactivar empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleReactivarEmpresa = async () => {
    if (!selectedEmpresa) return;
    setSaving(true);
    setActionError(null);
    try {
      await reactivarEmpresa(selectedEmpresa.nitId);
      setShowReactivarModal(false);
      setSelectedEmpresa(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al reactivar empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyFilteredList = () => {
    if (filteredEmpresas.length === 0) return;
    const text = filteredEmpresas.map((e) => `${e.nitId} - ${e.nombre}`).join('\n');
    navigator.clipboard.writeText(text);
    setCopiedList(true);
    setTimeout(() => setCopiedList(false), 2000);
  };

  const handleCopySingleNitNombre = (empresa: EmpresaSysadmin) => {
    const text = `${empresa.nitId} - ${empresa.nombre}`;
    navigator.clipboard.writeText(text);
    setCopiedNitNombreKey(empresa.nitId);
    setTimeout(() => setCopiedNitNombreKey(null), 2000);
  };

  if (isLoading) return <LoadingContainer text="Cargando empresas..." />;
  if (error) return <p className="text-red-600 text-center py-8">{error}</p>;

  return (
    <div className="space-y-4">
      {/* Barra superior */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto flex-1">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Buscar por nombre o NIT..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <div className="w-full sm:w-56">
            <Select
              value={selectedAsesorFilter}
              onChange={(e) => setSelectedAsesorFilter(e.target.value)}
              options={[
                { value: 'all', label: 'Todos los asesores' },
                { value: 'unassigned', label: 'Sin asesor asignado' },
                ...asesores
                  .filter((a) => a.rol === 'ASESOR')
                  .map((a) => ({
                    value: a.asesorId.toString(),
                    label: a.nombre,
                  })),
              ]}
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleCopyFilteredList}
            title="Copiar lista filtrada de empresas (NIT - Nombre)"
            className="flex items-center gap-1.5"
          >
            {copiedList ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            <span>{copiedList ? '¡Copiadas!' : 'Copiar lista'}</span>
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <ColumnPicker
            cols={MAIN_EMPRESAS_COLS}
            visible={colVisibleMain}
            onToggle={toggleColMain}
            onReset={resetColsMain}
            activeCount={colCountMain}
          />
          <p className="text-sm text-gray-500">{filteredEmpresas.length} empresas</p>
          <Button onClick={() => setShowCrearWizard(true)} size="sm">
            <Plus className="w-4 h-4 mr-1" />
            Nueva Empresa
          </Button>
        </div>
      </div>

      {/* Tabla */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: '800px' }}>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {colVisibleMain.nombre && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>}
                {colVisibleMain.nit && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NIT</th>}
                {colVisibleMain.nitNombre && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NIT - Empresa</th>
                )}
                {colVisibleMain.asesor && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asesor</th>}
                {colVisibleMain.combinacion && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Combinación NTC</th>
                )}
                {colVisibleMain.estado && <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>}
                {colVisibleMain.acciones && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredEmpresas.map((empresa) => (
                <tr key={empresa.nitId} className="hover:bg-gray-50">
                  {colVisibleMain.nombre && <td className="px-4 py-3 font-medium text-gray-900">{empresa.nombre}</td>}
                  {colVisibleMain.nit && <td className="px-4 py-3 text-gray-600">{empresa.nitId}</td>}
                  {colVisibleMain.nitNombre && (
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => handleCopySingleNitNombre(empresa)}
                        title="Clic para copiar NIT - Empresa"
                        className="group inline-flex items-center gap-1.5 px-2 py-1 rounded border border-gray-200 bg-gray-50 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 font-mono text-xs text-gray-700 transition-colors text-left"
                      >
                        <span>
                          {empresa.nitId} - {empresa.nombre}
                        </span>
                        {copiedNitNombreKey === empresa.nitId ? (
                          <Check className="w-3.5 h-3.5 text-green-600 shrink-0" />
                        ) : (
                          <Copy className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-600 shrink-0" />
                        )}
                      </button>
                    </td>
                  )}
                  {colVisibleMain.asesor && (
                    <td className="px-4 py-3">
                      {empresa.asesor ? (
                        <span className="text-gray-700">{empresa.asesor.nombre}</span>
                      ) : (
                        <span className="text-gray-400 italic">Sin asignar</span>
                      )}
                    </td>
                  )}
                  {colVisibleMain.combinacion && (
                    <td className="px-4 py-3">
                      {empresa.combinacionNtc ? (
                        <Badge variant="blue">{empresa.combinacionNtc.nombre}</Badge>
                      ) : (
                        <span className="text-gray-400 italic">Sin combinación</span>
                      )}
                    </td>
                  )}
                  {colVisibleMain.estado && (
                    <td className="px-4 py-3 text-center">
                      <Badge variant={empresa.estado ? 'green' : 'gray'}>{empresa.estado ? 'Activa' : 'Inactiva'}</Badge>
                    </td>
                  )}
                  {colVisibleMain.acciones && (
                    <td className="px-4 py-3 text-right">
                      <div className="relative inline-block" ref={openMenuNit === empresa.nitId ? menuRef : null}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            if (openMenuNit === empresa.nitId) {
                              setOpenMenuNit(null);
                              return;
                            }

                            const rect = e.currentTarget.getBoundingClientRect();
                            const estimatedHeight = 212;
                            const spaceBelow = window.innerHeight - rect.bottom;
                            const showAbove = spaceBelow < estimatedHeight + 12;

                            setMenuPosition({
                              top: showAbove ? rect.top - estimatedHeight - 4 : rect.bottom + 4,
                              left: rect.right - 224,
                            });
                            setOpenMenuNit(empresa.nitId);
                          }}
                          title="Acciones"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>

                        {openMenuNit === empresa.nitId && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setOpenMenuNit(null)} />
                            <div
                              className="fixed w-56 bg-white rounded-md shadow-lg border border-gray-200 z-50 py-1 text-left"
                              style={{ top: menuPosition.top, left: menuPosition.left }}
                            >
                              <button
                                onClick={() => openSincronizarModal(empresa)}
                                className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <RefreshCw className={`w-4 h-4 ${isSincronizandoEmpresa ? 'animate-spin' : 'text-blue-500'}`} />
                                Sincronizar cumplimiento
                              </button>
                              <button
                                onClick={() => openAsesorModal(empresa)}
                                className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <UserCheck className="w-4 h-4 text-green-500" />
                                Asignar asesor
                              </button>
                              <button
                                onClick={() => openCombinacionModal(empresa)}
                                className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Link2 className="w-4 h-4 text-indigo-500" />
                                Asignar NTCs
                              </button>
                              <button
                                onClick={() => openCambiarNtcModal(empresa)}
                                className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <RotateCcw className="w-4 h-4 text-orange-500" />
                                Cambiar combinación NTC
                              </button>
                              <div className="border-t border-gray-100 my-1" />
                              {empresa.estado ? (
                                <button
                                  onClick={() => openDeleteModal(empresa)}
                                  className="w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                  <PowerOff className="w-4 h-4" />
                                  Desactivar empresa
                                </button>
                              ) : (
                                <button
                                  onClick={() => openReactivarModal(empresa)}
                                  className="w-full px-4 py-2 text-sm text-green-700 hover:bg-green-50 flex items-center gap-2"
                                >
                                  <Power className="w-4 h-4" />
                                  Reactivar empresa
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Empresas desincronizadas */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Empresas desincronizadas</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Compara el porcentaje de cumplimiento actual contra el calculado para detectar empresas desincronizadas.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {desincronizadas.length > 0 && (
              <ColumnPicker
                cols={DESINCRONIZADAS_COLS}
                visible={colVisibleDesincronizadas}
                onToggle={toggleColDesincronizadas}
                onReset={resetColsDesincronizadas}
                activeCount={colCountDesincronizadas}
              />
            )}
            <Button onClick={handleBuscarDesincronizadas} disabled={isLoadingDesincronizadas} size="sm" variant="secondary">
              <RefreshCw className={`w-4 h-4 mr-1 ${isLoadingDesincronizadas ? 'animate-spin' : ''}`} />
              {isLoadingDesincronizadas ? 'Buscando...' : 'Buscar desincronizadas'}
            </Button>
          </div>
        </div>

        {(errorDesincronizadas ||
          recalcularError ||
          (hasSearchedDesincronizadas && !isLoadingDesincronizadas && desincronizadas.length === 0)) && (
          <div className="p-4 space-y-2">
            {errorDesincronizadas && <p className="text-red-600 text-sm">{errorDesincronizadas}</p>}
            {recalcularError && <p className="text-red-600 text-sm">{recalcularError}</p>}
            {hasSearchedDesincronizadas && !isLoadingDesincronizadas && desincronizadas.length === 0 && (
              <p className="text-sm text-gray-500">Todas las empresas estan sincronizadas.</p>
            )}
          </div>
        )}

        {desincronizadas.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: '700px' }}>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {colVisibleDesincronizadas.empresa && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                  )}
                  {colVisibleDesincronizadas.porcentaje && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% cumplimiento</th>
                  )}
                  {colVisibleDesincronizadas.sinAprobar && (
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Asesorias sin aprobar</th>
                  )}
                  {colVisibleDesincronizadas.acciones && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {desincronizadas.map((empresa) => (
                  <tr key={empresa.nitId} className="hover:bg-gray-50">
                    {colVisibleDesincronizadas.empresa && (
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{empresa.nombre}</p>
                        <p className="text-xs text-gray-500">{empresa.nitId}</p>
                      </td>
                    )}
                    {colVisibleDesincronizadas.porcentaje && (
                      <td className="px-4 py-3 text-right text-gray-700">
                        <p>
                          {empresa.porcentajeActual.toFixed(2)}% <span className="text-gray-400">actual</span>
                        </p>
                        <p>
                          {empresa.porcentajeCalculado.toFixed(2)}% <span className="text-gray-400">calculado</span>
                        </p>
                        <p className="text-xs text-gray-500">diferencia: {empresa.diferenciaPorcentaje.toFixed(2)}%</p>
                      </td>
                    )}
                    {colVisibleDesincronizadas.sinAprobar && (
                      <td className="px-4 py-3 text-center">
                        {empresa.asesoriasFinalizadasSinAprobar > 0 ? (
                          <Badge variant="warning">{empresa.asesoriasFinalizadasSinAprobar}</Badge>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                    )}
                    {colVisibleDesincronizadas.acciones && (
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={isSincronizandoEmpresa}
                          onClick={() => handleRecalcularDesincronizada(empresa.nitId)}
                        >
                          <RefreshCw className={`w-4 h-4 mr-1 ${recalculatingNit === empresa.nitId ? 'animate-spin' : ''}`} />
                          {recalculatingNit === empresa.nitId ? 'Recalculando...' : 'Recalcular'}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Empresas con horas de asesoria desincronizadas */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Empresas con horas desincronizadas</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Compara las horas de asesoria actuales contra el acumulado de todas las asesorias FINALIZADA de la empresa. Independiente de
              la deteccion de porcentaje.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {horasDesincronizadas.length > 0 && (
              <ColumnPicker
                cols={HORAS_DESINCRONIZADAS_COLS}
                visible={colVisibleHorasDesincronizadas}
                onToggle={toggleColHorasDesincronizadas}
                onReset={resetColsHorasDesincronizadas}
                activeCount={colCountHorasDesincronizadas}
              />
            )}
            <Button onClick={handleBuscarHorasDesincronizadas} disabled={isLoadingHorasDesincronizadas} size="sm" variant="secondary">
              <RefreshCw className={`w-4 h-4 mr-1 ${isLoadingHorasDesincronizadas ? 'animate-spin' : ''}`} />
              {isLoadingHorasDesincronizadas ? 'Buscando...' : 'Buscar horas desincronizadas'}
            </Button>
          </div>
        </div>

        {(errorHorasDesincronizadas ||
          recalcularHorasError ||
          (hasSearchedHorasDesincronizadas && !isLoadingHorasDesincronizadas && horasDesincronizadas.length === 0)) && (
          <div className="p-4 space-y-2">
            {errorHorasDesincronizadas && <p className="text-red-600 text-sm">{errorHorasDesincronizadas}</p>}
            {recalcularHorasError && <p className="text-red-600 text-sm">{recalcularHorasError}</p>}
            {hasSearchedHorasDesincronizadas && !isLoadingHorasDesincronizadas && horasDesincronizadas.length === 0 && (
              <p className="text-sm text-gray-500">Todas las empresas tienen las horas sincronizadas.</p>
            )}
          </div>
        )}

        {horasDesincronizadas.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: '500px' }}>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {colVisibleHorasDesincronizadas.empresa && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                  )}
                  {colVisibleHorasDesincronizadas.horas && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Horas asesoria</th>
                  )}
                  {colVisibleHorasDesincronizadas.acciones && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {horasDesincronizadas.map((empresa) => (
                  <tr key={empresa.nitId} className="hover:bg-gray-50">
                    {colVisibleHorasDesincronizadas.empresa && (
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{empresa.nombre}</p>
                        <p className="text-xs text-gray-500">{empresa.nitId}</p>
                      </td>
                    )}
                    {colVisibleHorasDesincronizadas.horas && (
                      <td className="px-4 py-3 text-right text-gray-700">
                        <p>
                          {empresa.horasActual.toFixed(2)}h <span className="text-gray-400">actual</span>
                        </p>
                        <p>
                          {empresa.horasCalculadas.toFixed(2)}h <span className="text-gray-400">calculado</span>
                        </p>
                        <p className="text-xs text-gray-500">diferencia: {empresa.diferenciaHoras.toFixed(2)}h</p>
                      </td>
                    )}
                    {colVisibleHorasDesincronizadas.acciones && (
                      <td className="px-4 py-3 text-right">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={isRecalculandoHoras}
                          onClick={() => handleRecalcularHoras(empresa.nitId)}
                        >
                          <RefreshCw className={`w-4 h-4 mr-1 ${recalculatingHorasNit === empresa.nitId ? 'animate-spin' : ''}`} />
                          {recalculatingHorasNit === empresa.nitId ? 'Recalculando...' : 'Recalcular'}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Asesorias sin evaluacion aprobada (control y seguimiento) */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Asesorias sin evaluacion aprobada</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Asesorias FINALIZADA cuya Evaluacion/acta final nunca quedo APROBADA. Vista de solo lectura para control y seguimiento — no
              corrige nada.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {asesoriasSinEvaluacion.length > 0 && (
              <ColumnPicker
                cols={SIN_EVALUACION_COLS}
                visible={colVisibleSinEvaluacion}
                onToggle={toggleColSinEvaluacion}
                onReset={resetColsSinEvaluacion}
                activeCount={colCountSinEvaluacion}
              />
            )}
            <Button onClick={handleBuscarSinEvaluacion} disabled={isLoadingSinEvaluacion} size="sm" variant="secondary">
              <RefreshCw className={`w-4 h-4 mr-1 ${isLoadingSinEvaluacion ? 'animate-spin' : ''}`} />
              {isLoadingSinEvaluacion ? 'Buscando...' : 'Buscar'}
            </Button>
          </div>
        </div>

        {(errorSinEvaluacion || (hasSearchedSinEvaluacion && !isLoadingSinEvaluacion && asesoriasSinEvaluacion.length === 0)) && (
          <div className="p-4 space-y-2">
            {errorSinEvaluacion && <p className="text-red-600 text-sm">{errorSinEvaluacion}</p>}
            {hasSearchedSinEvaluacion && !isLoadingSinEvaluacion && asesoriasSinEvaluacion.length === 0 && (
              <p className="text-sm text-gray-500">Todas las asesorias FINALIZADA tienen su evaluacion aprobada.</p>
            )}
          </div>
        )}

        {asesoriasSinEvaluacion.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: '700px' }}>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {colVisibleSinEvaluacion.empresa && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Empresa</th>
                  )}
                  {colVisibleSinEvaluacion.asesoria && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asesoria</th>
                  )}
                  {colVisibleSinEvaluacion.fecha && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha fin</th>
                  )}
                  {colVisibleSinEvaluacion.horas && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Horas</th>
                  )}
                  {colVisibleSinEvaluacion.evaluacion && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado evaluacion</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {asesoriasSinEvaluacion.map((a) => (
                  <tr key={a.asesoriaId} className="hover:bg-gray-50">
                    {colVisibleSinEvaluacion.empresa && (
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{a.nombreEmpresa}</p>
                        <p className="text-xs text-gray-500">{a.nitId}</p>
                      </td>
                    )}
                    {colVisibleSinEvaluacion.asesoria && <td className="px-4 py-3 text-gray-700">#{a.asesoriaId}</td>}
                    {colVisibleSinEvaluacion.fecha && (
                      <td className="px-4 py-3 text-gray-700">{a.fechaFin ? new Date(a.fechaFin).toLocaleDateString('es-CO') : '—'}</td>
                    )}
                    {colVisibleSinEvaluacion.horas && <td className="px-4 py-3 text-right text-gray-700">{a.horas.toFixed(2)}h</td>}
                    {colVisibleSinEvaluacion.evaluacion && (
                      <td className="px-4 py-3">
                        <Badge variant="warning">{a.evaluacionEstado ?? 'Sin evaluacion'}</Badge>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal desactivar empresa */}
      <Modal isOpen={showDeleteModal} onClose={() => !saving && setShowDeleteModal(false)} title="Desactivar Empresa">
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-100">
            <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-yellow-900">La empresa quedará inactiva</p>
              <p className="text-xs text-yellow-700 mt-0.5">
                Estás a punto de desactivar a <strong>{selectedEmpresa?.nombre}</strong> (NIT: {selectedEmpresa?.nitId}).
              </p>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            La empresa no aparecerá en los listados activos ni podrá recibir nuevas asesorías. Los datos se conservan y la empresa puede
            reactivarse en cualquier momento.
          </p>

          <p className="text-sm font-medium text-gray-800">¿Confirmas que deseas desactivar esta empresa?</p>

          {actionError && <p className="text-red-600 text-sm">{actionError}</p>}

          <div className="flex gap-3 justify-end pt-2">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleEliminarEmpresa} disabled={saving} className="bg-yellow-600 hover:bg-yellow-700 text-white">
              {saving ? 'Desactivando...' : 'Sí, Desactivar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal reactivar empresa */}
      <Modal isOpen={showReactivarModal} onClose={() => !saving && setShowReactivarModal(false)} title="Reactivar Empresa">
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
            <Power className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-green-900">La empresa volverá a estar activa</p>
              <p className="text-xs text-green-700 mt-0.5">
                Estás a punto de reactivar a <strong>{selectedEmpresa?.nombre}</strong> (NIT: {selectedEmpresa?.nitId}).
              </p>
            </div>
          </div>

          <p className="text-sm text-gray-600">La empresa volverá a aparecer en los listados activos y podrá recibir nuevas asesorías.</p>

          {actionError && <p className="text-red-600 text-sm">{actionError}</p>}

          <div className="flex gap-3 justify-end pt-2">
            <Button variant="secondary" onClick={() => setShowReactivarModal(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleReactivarEmpresa} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white">
              {saving ? 'Reactivando...' : 'Sí, Reactivar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal asignar asesor */}
      <Modal isOpen={showAsesorModal} onClose={() => setShowAsesorModal(false)} title={`Asignar asesor a ${selectedEmpresa?.nombre || ''}`}>
        <div className="space-y-4">
          {actionError && <p className="text-red-600 text-sm">{actionError}</p>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Asesor</label>
            {loadingAsesores ? (
              <p className="text-gray-500 text-sm">Cargando asesores...</p>
            ) : (
              <Select
                value={selectedAsesorId}
                onChange={(e) => setSelectedAsesorId(e.target.value)}
                options={[
                  { value: '', label: '— Sin asesor —' },
                  ...asesores
                    .filter((a) => a.estado && a.rol === 'ASESOR')
                    .map((a) => ({
                      value: a.asesorId.toString(),
                      label: `${a.nombre} (${a.cantidadEmpresas} empresas)`,
                    })),
                ]}
              />
            )}
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="secondary" onClick={() => setShowAsesorModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAsignarAsesor} disabled={saving}>
              {saving ? 'Guardando...' : 'Asignar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal asignar combinación NTC */}
      <Modal
        isOpen={showCombinacionModal}
        onClose={() => setShowCombinacionModal(false)}
        title={`Asignar NTCs a ${selectedEmpresa?.nombre || ''}`}
      >
        <div className="space-y-4">
          {actionError && <p className="text-red-600 text-sm">{actionError}</p>}

          <p className="text-sm text-gray-600">
            Selecciona la combinación de NTCs que aplican a esta empresa. Esto define qué requisitos deberá cumplir.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Combinación de NTCs</label>
            {loadingCombinaciones ? (
              <p className="text-gray-500 text-sm">Cargando combinaciones...</p>
            ) : (
              <Select
                value={selectedCombinacionId}
                onChange={(e) => setSelectedCombinacionId(e.target.value)}
                options={[
                  { value: '', label: '— Sin combinación —' },
                  ...combinaciones.map((c) => ({
                    value: c.combinacionId.toString(),
                    label: c.nombre,
                  })),
                ]}
              />
            )}
          </div>

          {/* Detalle de la combinación seleccionada */}
          {selectedCombinacionId && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">NTCs incluidas</p>
              <div className="space-y-1">
                {combinaciones
                  .find((c) => c.combinacionId.toString() === selectedCombinacionId)
                  ?.ntcs.map((ntc) => (
                    <div key={ntc.ntcId} className="flex justify-between items-center text-sm">
                      <span className="text-gray-700">{ntc.nombre}</span>
                      <span className="text-gray-400">{ntc.totalRequisitos} requisitos</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <Button variant="secondary" onClick={() => setShowCombinacionModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAsignarCombinacion} disabled={saving}>
              {saving ? 'Guardando...' : 'Asignar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal sincronizar empresa */}
      <Modal
        isOpen={showSincronizarModal}
        onClose={() => !saving && setShowSincronizarModal(false)}
        title={`Sincronizar ${selectedEmpresa?.nombre || ''}`}
      >
        <div className="space-y-4">
          {actionError && <p className="text-red-600 text-sm">{actionError}</p>}

          <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
            <RefreshCw className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <p className="text-sm text-blue-900">
              Se reconstruirá el estado de cumplimiento basándose en asesorías FINALIZADAS y APROBADAS en orden cronológico correcto.
            </p>
          </div>

          <p className="text-sm text-gray-600">¿Deseas sincronizar cumplimiento para {selectedEmpresa?.nombre}?</p>

          <div className="flex gap-3 justify-end pt-2">
            <Button variant="secondary" onClick={() => setShowSincronizarModal(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSincronizarEmpresa} disabled={saving}>
              {saving ? 'Sincronizando...' : 'Sincronizar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Wizard crear empresa */}
      <CrearEmpresaWizard isOpen={showCrearWizard} onClose={() => setShowCrearWizard(false)} crearEmpresa={crearEmpresa} />

      {/* Modal cambiar combinación NTC */}
      <CambiarCombinacionNtcModal
        isOpen={showCambiarNtcModal}
        onClose={() => setShowCambiarNtcModal(false)}
        empresa={selectedEmpresa}
        cambiarCombinacionNtc={cambiarCombinacionNtc}
      />
    </div>
  );
}
